import { Paper, Title, Text, Badge, Group, Stack, Grid, Box, SimpleGrid } from "@mantine/core";
import { IconCircleCheck, IconAlertCircle } from "@tabler/icons-react";
import { toColorVisionCorrectnessArray } from "@utils/test-specific-utils/ColorVisionTestUtils";
import { buildColorVisionMetrics } from "@components/testContainer/result/utils/colorVisionMetrics";

interface ColorVisionTestCardProps {
  test: any;
  colorVisionImages: string[];
}

export function ColorVisionTestCard({ test, colorVisionImages }: ColorVisionTestCardProps) {
  if (!test.hasColorVisionData || !test.colorVisionResults) return null;
  const normalizedResults = toColorVisionCorrectnessArray(test.colorVisionResults);
  const metrics = buildColorVisionMetrics(normalizedResults.map((v) => v === true));
  const statusLabel =
    metrics.screeningClass === "normal"
      ? "Normal red-green screening"
      : metrics.screeningClass === "deficiency"
        ? "Likely red-green deficiency"
        : metrics.screeningClass === "borderline"
          ? "Borderline result"
          : "Insufficient data";
  const statusColor =
    metrics.screeningClass === "normal"
      ? "green"
      : metrics.screeningClass === "deficiency"
        ? "red"
        : "yellow";

  // Filter to show only incorrect answers, limit to 20
  const incorrectAnswers = normalizedResults
    .map((result: boolean | null, index: number) => ({ result, index }))
    .filter((item: any) => item.result === false)
    .slice(0, 20);

  return (
    <Grid gutter="md">
      <Grid.Col span={5}>
        <Stack gap="md">
          <Group justify="space-between" wrap="nowrap" align="center">
            <Group gap="xs" wrap="nowrap">
              <Title order={4}>{test.title}</Title>
              {test.status === "normal" ? (
                <Badge color="green" variant="filled" leftSection={<IconCircleCheck size={14} />}>
                  Normal
                </Badge>
              ) : (
                <Badge color="orange" variant="filled" leftSection={<IconAlertCircle size={14} />}>
                  Attention
                </Badge>
              )}
            </Group>
            <Group gap={4} wrap="nowrap" align="baseline">
              <Text size="xs" c="green.7" fw={600}>
                Score
              </Text>
              <Text size="lg" c="green.7" fw={700}>
                {test.score}
              </Text>
            </Group>
          </Group>
          <Text c="dimmed" size="sm">
            {test.description}
          </Text>

          <Paper withBorder p="sm" radius="md">
            <Stack gap="xs">
              <Group justify="space-between">
                <Badge color={statusColor} variant="filled">
                  {statusLabel}
                </Badge>
              </Group>
              <Text size="sm">
                Main score (1-{metrics.screening.total}): {metrics.screening.correct}/
                {metrics.screening.total} ({metrics.screening.errors} errors)
              </Text>
              <SimpleGrid cols={4} spacing="xs">
                <Paper withBorder p="xs" radius="sm">
                  <Text size="xs" c="blue.7" fw={700}>
                    Transformation
                  </Text>
                  <Text size="sm" fw={700}>
                    {metrics.transformation.correct}/{metrics.transformation.total}
                  </Text>
                </Paper>
                <Paper withBorder p="xs" radius="sm">
                  <Text size="xs" c="green.7" fw={700}>
                    Vanishing
                  </Text>
                  <Text size="sm" fw={700}>
                    {metrics.vanishing.correct}/{metrics.vanishing.total}
                  </Text>
                </Paper>
                <Paper withBorder p="xs" radius="sm">
                  <Text size="xs" c="yellow.8" fw={700}>
                    Hidden Digit
                  </Text>
                  <Text size="sm" fw={700}>
                    {metrics.hidden.total > 0
                      ? `${metrics.hidden.correct}/${metrics.hidden.total}`
                      : "N/A"}
                  </Text>
                </Paper>
                <Paper withBorder p="xs" radius="sm">
                  <Text size="xs" c="red.7" fw={700}>
                    Typing Clues
                  </Text>
                  <Text size="sm" fw={700}>
                    {metrics.typing.total > 0
                      ? `${metrics.typing.correct}/${metrics.typing.total}`
                      : "N/A"}
                  </Text>
                </Paper>
              </SimpleGrid>
            </Stack>
          </Paper>

          <Group align="stretch" wrap="nowrap" gap="sm">
            <Box style={{ flex: 1 }}>
              <Text size="sm" fw={600} mb="xs">
                Results
              </Text>
              <Text size="sm" c="dimmed">
                {test.details}
              </Text>
            </Box>

            <Box style={{ flex: 1 }}>
              <Text size="sm" fw={600} mb="xs">
                Recommendation
              </Text>
              <Text size="sm" c="dimmed">
                {test.recommendation}
              </Text>
            </Box>
          </Group>
        </Stack>
      </Grid.Col>

      <Grid.Col span={7}>
        {incorrectAnswers.length === 0 ? (
          <Paper p="xl" radius="md" withBorder>
            <Stack align="center" gap="md">
              <IconCircleCheck size={48} color="green" />
              <Text size="lg" fw={500}>
                All plates identified correctly!
              </Text>
            </Stack>
          </Paper>
        ) : (
          <Box>
            <Text size="sm" fw={600} mb="md" c="red.7">
              Incorrect Answers ({incorrectAnswers.length})
            </Text>
            <SimpleGrid cols={6} spacing="xl">
              {incorrectAnswers.map(({ index }: any) => (
                <Stack key={index} gap="xs" align="center">
                  <Badge color="red" size="sm" variant="filled">
                    Plate {index + 1}
                  </Badge>
                  {colorVisionImages[index] && (
                    <Box style={{ borderRadius: "50%", overflow: "hidden", aspectRatio: "1" }}>
                      <img
                        src={colorVisionImages[index]}
                        alt={`Ishihara Plate ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              ))}
            </SimpleGrid>
          </Box>
        )}
      </Grid.Col>
    </Grid>
  );
}
